// src/hooks/useSessionMedia.js
import { smoothedRotationAt } from '../rotationHelper.js';
import { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';
import { interpolateAngle, renderVideoToCanvas } from '../mediaHelper.js';
import { initHlsPlayer, setupAudioRouting } from '../hlsHelper.js';
import { formatTime } from '../uiHelper.js';
import { accessHeaders, captureSessionAccess } from '../sessionAccess.js';
import {
    bufferedAheadAt,
    mediaRangesToSessionElapsed,
    mediaTimeToSessionElapsed,
    sessionElapsedToMediaTime,
} from '../mediaClockHelper.js';

export const useSessionMedia = (sessionId, BACKEND_API_URL, sessionPswdKeyRef) => {
    // En-têtes d'accès v2 (jeton), calculés une fois : hls.js les ajoute à chaque requête.
    const accessHeadersRef = useRef({});
    useEffect(() => {
        accessHeaders().then(headers => { accessHeadersRef.current = headers; }).catch(() => {});
    }, []);
    const hlsRef = useRef(null);
    const isMediaActiveRef = useRef(false);
    const audioContextRef = useRef(null);
    const gainNodeRef = useRef(null);
    const audioSourceRef = useRef(null);
    const isFirstPlayRef = useRef(true);
    const hlsLatencyRef = useRef(12); // Latence HLS mesurée dynamiquement (défaut 12s)

    // 0. GESTION DOUBLE CAMÉRA (arrière vs avant)
    const [isDualCameraAvailable, setIsDualCameraAvailable] = useState(false);
    const [selectedCamera, setSelectedCamera] = useState('back'); // 'back' | 'front'
    const selectedCameraRef = useRef('back');
    selectedCameraRef.current = selectedCamera;

    // 1. ÉTAT MAÎTRE (Timeline GPS)
    const [timelineState, setTimelineState] = useState({
        value: 0,
        max: 0,
        display: "⏱ 00:00 / 00:00",
        gradient: null,
        minTs: null,
        maxTs: null,
        mediaOffset: 0,
        isLive: true
    });
    const timelineStateRef = useRef(timelineState);
    timelineStateRef.current = timelineState;

    // 2. ÉTAT ESCLAVE (Progression Audio/Vidéo dans le clip)
    const [slaveState, setSlaveState] = useState({
        position: 0,
        duration: 0,
        display: null, // "Démarre dans X s"
        volume: 50
    });
    const slaveStateRef = useRef(slaveState);
    slaveStateRef.current = slaveState;

    const targetRotationRef = useRef(0);
    const smoothedRotationRef = useRef(0);
    const id3SegmentsMapRef = useRef(new Map()); // Map<fragSn, { start, end, cues: [{ pts, rot, ts }] }>
    const id3CuesTimelineRef = useRef([]); // Liste triée à plat de tous les cues
    const currentVideoTsRef = useRef(null);
    const animationFrameIdRef = useRef(null);
    const mediaProgressUpdaterRef = useRef(null);
    const recentFragmentDurationsRef = useRef([]);
    const mediaRetryTimerRef = useRef(null);
    const mediaRetryAttemptRef = useRef(0);
    const maxSeenDurationRef = useRef(0);
    const ctxRef = useRef(null);
    const domRef = useRef(null);
    const mediaFrameVisibleRef = useRef(false);
    const resizeObserverRef = useRef(null);

    const clearCanvas = (dom = domRef.current) => {
        if (!dom?.canvas) return;
        const ctx = ctxRef.current || dom.canvas.getContext('2d');
        if (ctx) ctx.clearRect(0, 0, dom.canvas.width, dom.canvas.height);
    };

    const setMediaFrameVisible = (visible, dom = domRef.current) => {
        mediaFrameVisibleRef.current = Boolean(visible);
        if (!visible) clearCanvas(dom);
    };

    const rebuildCuesTimeline = () => {
        const allCues = [];
        for (const seg of id3SegmentsMapRef.current.values()) {
            if (seg.cues && seg.cues.length > 0) {
                for (const cue of seg.cues) {
                    allCues.push(cue);
                }
            }
        }
        allCues.sort((a, b) => a.pts - b.pts);
        id3CuesTimelineRef.current = allCues;
    };

    const getRotationAtTime = (currentTime) => {
        const cues = id3CuesTimelineRef.current;
        if (!cues || cues.length === 0) {
            return targetRotationRef.current || 0;
        }
        // Angle interpolé entre le cue précédent et le suivant, lissé sur une fenêtre
        // centrée autour de l'image affichée : sans tremblement ni retard.
        const smoothed = smoothedRotationAt(cues, currentTime);
        return smoothed === null ? cues[0].rot : smoothed;
    };

    const getSessionElapsedAtMediaTime = (mediaTime, sessionStartTs, fallbackOffset = 0) => (
        mediaTimeToSessionElapsed({
            mediaTime,
            cues: id3CuesTimelineRef.current,
            sessionStartTs,
            fallbackOffset,
        })
    );

    const getMediaTimeAtSessionElapsed = (sessionElapsed, sessionStartTs, fallbackOffset = 0) => (
        sessionElapsedToMediaTime({
            sessionElapsed,
            cues: id3CuesTimelineRef.current,
            sessionStartTs,
            fallbackOffset,
        })
    );

    const renderSingleFrame = (dom, forceSnap = false) => {
        if (!dom || !dom.canvas || !dom.video) return;
        if (!mediaFrameVisibleRef.current) {
            clearCanvas(dom);
            return;
        }
        if (!dom.video.videoWidth || !dom.video.videoHeight) return;
        if (!ctxRef.current) ctxRef.current = dom.canvas.getContext("2d");

        const rot = getRotationAtTime(dom.video.currentTime);
        targetRotationRef.current = rot;

        let appliedRotation;
        if (rotationModeRef.current === 'locked') {
            // Mode cardinal fluide (0°, 90°, 180°, 270°)
            const normAngle = ((rot % 360) + 360) % 360;
            const targetCardinal = Math.round(normAngle / 90) * 90 % 360;
            if (forceSnap) {
                appliedRotation = targetCardinal;
            } else {
                appliedRotation = interpolateAngle(smoothedRotationRef.current, targetCardinal, 0.10);
            }
        } else {
            // Mode libre : angle déjà interpolé et lissé sans retard (getRotationAtTime),
            // appliqué tel quel ; un lissage supplémentaire le décalerait de l'image.
            appliedRotation = ((rot % 360) + 360) % 360;
        }
        smoothedRotationRef.current = appliedRotation;

        renderVideoToCanvas(
            ctxRef.current,
            dom.video,
            dom.canvas,
            appliedRotation,
            !!document.fullscreenElement,
            true
        );
    };

    const mediaUrl = (filename, cacheBust = false) => {
        // v1 : linkkey en paramètre ; v2 : jeton en en-tête (xhrSetup de hls.js).
        const access = captureSessionAccess();
        const params = new URLSearchParams();
        if (cacheBust) params.set('cb', String(Date.now()));
        const query = params.toString();
        return `${BACKEND_API_URL}/api/session/${encodeURIComponent(sessionId)}/media/${encodeURIComponent(filename)}${query ? `?${query}` : ''}`;
    };

    const getNativeBufferedRanges = (video = domRef.current?.video) => {
        if (!video?.buffered) return [];
        const ranges = [];
        for (let index = 0; index < video.buffered.length; index += 1) {
            ranges.push({
                start: video.buffered.start(index),
                end: video.buffered.end(index),
            });
        }
        return ranges;
    };

    const getBufferedAhead = (mediaTime, video = domRef.current?.video) => {
        if (!video) return 0;
        const nativeAhead = bufferedAheadAt(getNativeBufferedRanges(video), mediaTime);
        if (nativeAhead > 0) return nativeAhead;
        // Détection pour WebView / WebKit où buffered est vide tant que le média est en pause
        if (video.readyState >= 3) return 3.0;
        if (video.readyState >= 2) return 1.5;
        if (video.seekable?.length > 0) {
            const seekEnd = video.seekable.end(video.seekable.length - 1);
            const seekStart = video.seekable.start(0);
            const time = typeof mediaTime === 'number' && Number.isFinite(mediaTime) ? mediaTime : 0;
            if (time >= seekStart - 0.25 && time <= seekEnd + 0.1) {
                return Math.max(0.5, seekEnd - time);
            }
        }
        return 0;
    };

    const getRequiredDirectBufferSeconds = () => {
        const durations = recentFragmentDurationsRef.current
            .filter(value => Number.isFinite(value) && value > 0)
            .slice(-6)
            .sort((left, right) => left - right);
        const median = durations.length > 0
            ? durations[Math.floor(durations.length / 2)]
            : 3;
        // Deux segments complets au minimum. Pour les segments Android de 3 s,
        // le Direct démarre donc avec 6 s d'avance.
        return Math.max(4, Math.min(12, median * 2));
    };

    const getSafeDirectMediaTime = (video = domRef.current?.video) => {
        const ranges = getNativeBufferedRanges(video);
        const lastRange = ranges[ranges.length - 1];
        if (!lastRange) return null;
        return Math.max(
            lastRange.start,
            lastRange.end - getRequiredDirectBufferSeconds()
        );
    };

    const hasReceivedMediaData = (video = domRef.current?.video) => Boolean(
        video && (
            getNativeBufferedRanges(video).length > 0
            || (video.seekable?.length || 0) > 0
            || video.readyState >= 2
        )
    );

    // Détection de la présence du flux double caméra (playlist_front.m3u8)
    useEffect(() => {
        if (!sessionId) return;
        let isCancelled = false;
        const probeFrontCamera = async () => {
            try {
                const url = mediaUrl('playlist_front.m3u8', true);
                const headers = { ...accessHeadersRef.current, Range: 'bytes=0-10' };
                const res = await fetch(url, { method: 'GET', headers });
                if (!isCancelled) {
                    if (res.status === 200 || res.status === 206) {
                        setIsDualCameraAvailable(true);
                    } else if (res.status === 404) {
                        setIsDualCameraAvailable(false);
                    }
                }
            } catch {
                // Ignore probe failure
            }
        };

        probeFrontCamera();
        const probeInterval = setInterval(probeFrontCamera, 10000);
        return () => {
            isCancelled = true;
            clearInterval(probeInterval);
        };
    }, [sessionId]);

    const getActivePlaylistFilename = () => {
        return selectedCameraRef.current === 'front' ? 'playlist_front.m3u8' : 'playlist.m3u8';
    };

    const ensureMediaLoading = (immediate = false) => {
        if (hasReceivedMediaData() || mediaRetryTimerRef.current) return;
        const delay = immediate
            ? 0
            : Math.min(5000, 750 * (2 ** Math.min(mediaRetryAttemptRef.current, 3)));
        mediaRetryTimerRef.current = setTimeout(() => {
            mediaRetryTimerRef.current = null;
            if (hasReceivedMediaData()) return;
            mediaRetryAttemptRef.current += 1;
            const filename = getActivePlaylistFilename();
            if (hlsRef.current) {
                hlsRef.current.loadSource(mediaUrl(filename, true));
                hlsRef.current.startLoad?.();
            } else if (domRef.current?.video) {
                const video = domRef.current.video;
                video.src = mediaUrl(filename, true);
                video.load();
                video.play().catch(() => {});
            }
        }, delay);
    };

    const switchCamera = (camera) => {
        const targetCam = (camera === 'front' || camera === 'back')
            ? camera
            : (selectedCameraRef.current === 'back' ? 'front' : 'back');

        if (targetCam === selectedCameraRef.current) return;
        setSelectedCamera(targetCam);
        selectedCameraRef.current = targetCam;

        const filename = targetCam === 'front' ? 'playlist_front.m3u8' : 'playlist.m3u8';
        const newUrl = mediaUrl(filename, true);

        if (hlsRef.current) {
            hlsRef.current.loadSource(newUrl);
            hlsRef.current.startLoad?.();
            if (domRef.current?.video && !domRef.current.video.paused) {
                domRef.current.video.play().catch(() => {});
            }
        } else if (domRef.current?.video) {
            const video = domRef.current.video;
            const wasPlaying = !video.paused;
            video.src = newUrl;
            video.load();
            if (wasPlaying) {
                video.play().catch(() => {});
            }
        }
    };

    const initializeMedia = (dom) => {
        if (!dom.video) return;
        domRef.current = dom;
        isMediaActiveRef.current = true;
        setMediaFrameVisible(false, dom);
        dom.video.muted = true;
        dom.video.addEventListener("loadedmetadata", () => adaptCanvasToFullscreen(dom, false), { once: true });

        const playlistUrl = mediaUrl(getActivePlaylistFilename());
        hlsRef.current = initHlsPlayer(
            dom.video,
            playlistUrl,
            sessionPswdKeyRef.current,
            BACKEND_API_URL,
            undefined,
            accessHeadersRef.current
        );

        if (hlsRef.current) {
            const markMediaProgress = () => {
                mediaRetryAttemptRef.current = 0;
                if (mediaRetryTimerRef.current) {
                    clearTimeout(mediaRetryTimerRef.current);
                    mediaRetryTimerRef.current = null;
                }
            };

            hlsRef.current.on(Hls.Events.MANIFEST_PARSED, markMediaProgress);
            hlsRef.current.on(Hls.Events.FRAG_BUFFERED, (event, data) => {
                markMediaProgress();
                const duration = Number(data?.frag?.duration);
                if (Number.isFinite(duration) && duration > 0) {
                    recentFragmentDurationsRef.current = [
                        ...recentFragmentDurationsRef.current.slice(-5),
                        duration,
                    ];
                }
                mediaProgressUpdaterRef.current?.();
            });
            hlsRef.current.on(Hls.Events.ERROR, (event, data) => {
                if (!data?.fatal) return;
                if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
                    hlsRef.current?.recoverMediaError();
                } else if (!hasReceivedMediaData()) {
                    ensureMediaLoading();
                }
            });

            // Écoute des métadonnées ID3 démultiplexées par Hls.js
            hlsRef.current.on(Hls.Events.FRAG_PARSING_METADATA, (event, data) => {
                try {
                    if (data && data.samples && data.samples.length > 0) {
                        const frag = data.frag;
                        const fragKey = (frag && typeof frag.sn === 'number') ? frag.sn : (frag ? frag.start : 'frag_' + Date.now());
                        const fragStart = (frag && typeof frag.start === 'number') ? frag.start : 0;
                        const fragDuration = (frag && typeof frag.duration === 'number') ? frag.duration : 3;

                        const segmentCues = [];
                        for (const sample of data.samples) {
                            if (sample.data) {
                                const rawStr = new TextDecoder('utf-8').decode(sample.data);
                                console.log('[ID3 DEMUX]', `frag.sn: ${frag?.sn}`, `pts: ${sample.pts}`, rawStr);
                                // Angle entier (anciens enregistrements) ou au dixième de degré.
                                const matchRot = rawStr.match(/ROTATE:(\d+(?:\.\d+)?)/i);
                                const matchTs = rawStr.match(/TS:(\d+)/i);
                                const rot = matchRot ? parseFloat(matchRot[1]) : null;
                                const ts = matchTs ? parseInt(matchTs[1], 10) : null;

                                if (rot !== null) {
                                    const samplePts = (typeof sample.pts === 'number' && !isNaN(sample.pts))
                                        ? sample.pts
                                        : fragStart;
                                    segmentCues.push({
                                        pts: samplePts,
                                        rot: ((rot % 360) + 360) % 360,
                                        ts: ts
                                    });
                                }
                                if (ts !== null && !isNaN(ts)) {
                                    currentVideoTsRef.current = ts;
                                }
                            }
                        }

                        if (segmentCues.length > 0) {
                            id3SegmentsMapRef.current.set(fragKey, {
                                sn: fragKey,
                                start: fragStart,
                                end: fragStart + fragDuration,
                                cues: segmentCues
                            });
                            rebuildCuesTimeline();
                            mediaProgressUpdaterRef.current?.();

                            // Si le lecteur est en pause ou en seek, actualiser immédiatement l'affichage
                            if (dom.video && dom.video.paused) {
                                renderSingleFrame(dom);
                            }
                        }
                    }
                } catch (e) {
                    console.error('[ID3] Erreur parsing ID3 metadata:', e);
                }
            });

            hlsRef.current.on(Hls.Events.LEVEL_LOADED, (event, data) => {
                if (data.details && data.details.live) {
                    const fragments = data.details.fragments;
                    if (fragments && fragments.length > 0) {
                        const lastFrag = fragments[fragments.length - 1];
                        const liveEdge = lastFrag.start + lastFrag.duration;
                        if (dom.video && dom.video.currentTime > 0) {
                            const measured = liveEdge - dom.video.currentTime;
                            if (measured > 0 && measured < 30) {
                                hlsLatencyRef.current = Math.round(measured);
                            }
                        }
                    }
                }
            });
        } else if (dom.video?.textTracks) {
            const handleTextTrackAdd = (e) => {
                const track = e.track;
                if (track && (track.kind === 'metadata' || track.kind === 'subtitles' || track.kind === 'captions')) {
                    track.mode = 'hidden';
                    track.addEventListener('cuechange', () => {
                        if (track.activeCues && track.activeCues.length > 0) {
                            for (let i = 0; i < track.activeCues.length; i++) {
                                const cue = track.activeCues[i];
                                let text = '';
                                if (cue.text) text = cue.text;
                                else if (cue.data) {
                                    try { text = new TextDecoder('utf-8').decode(cue.data); } catch(err){}
                                } else if (cue.value) {
                                    text = typeof cue.value === 'string' ? cue.value : JSON.stringify(cue.value);
                                }
                                const matchRot = text.match(/ROTATE:(\d+(?:\.\d+)?)/i);
                                const matchTs = text.match(/TS:(\d+)/i);
                                const rot = matchRot ? parseFloat(matchRot[1]) : null;
                                const ts = matchTs ? parseInt(matchTs[1], 10) : null;
                                if (rot !== null) {
                                    targetRotationRef.current = ((rot % 360) + 360) % 360;
                                }
                                if (ts !== null && !isNaN(ts)) {
                                    currentVideoTsRef.current = ts;
                                }
                            }
                        }
                    });
                }
            };
            dom.video.textTracks.addEventListener('addtrack', handleTextTrackAdd);
            for (let i = 0; i < dom.video.textTracks.length; i++) {
                handleTextTrackAdd({ track: dom.video.textTracks[i] });
            }
        }

        if (!audioContextRef.current) {
            const { audioCtx, gainNode, source } = setupAudioRouting(dom.video, slaveStateRef.current.volume);
            audioContextRef.current = audioCtx;
            gainNodeRef.current = gainNode;
            audioSourceRef.current = source;
        }
    };

    // Mode de rotation : 'locked' par défaut (portrait / paysage cardinal 0, 90, 180, 270) ou 'auto' (horizon fluide au degré près)
    const [rotationMode, setRotationMode] = useState('locked');
    const rotationModeRef = useRef('locked');
    rotationModeRef.current = rotationMode;

    const toggleRotationMode = () => {
        setRotationMode(prev => {
            const nextMode = prev === 'locked' ? 'auto' : 'locked';
            rotationModeRef.current = nextMode;
            if (domRef.current) {
                renderSingleFrame(domRef.current, true);
            }
            return nextMode;
        });
    };

    const drawFrame = (dom) => {
        if (!dom.canvas || !dom.video) return;

        // Empêcher la multiplication des boucles de rendu
        if (animationFrameIdRef.current) {
            cancelAnimationFrame(animationFrameIdRef.current);
            animationFrameIdRef.current = null;
        }

        const renderLoop = () => {
            if (!dom.canvas || !dom.video || dom.video.paused) {
                animationFrameIdRef.current = null;
                return;
            }

            animationFrameIdRef.current = requestAnimationFrame(renderLoop);
            renderSingleFrame(dom);
        };

        renderLoop();
    };

    const adaptCanvasToFullscreen = (dom, isFullscreen) => {
        if (!dom?.canvas) return;
        const wrapper = dom.canvasWrapper;
        const width = isFullscreen ? window.innerWidth : (wrapper?.clientWidth || window.innerWidth);
        const height = isFullscreen ? window.innerHeight : (wrapper?.clientHeight || window.innerHeight);
        const dpr = Math.min(window.devicePixelRatio || 1, 2);

        if (width > 0 && height > 0) {
            dom.canvas.width = Math.round(width * dpr);
            dom.canvas.height = Math.round(height * dpr);
            dom.canvas.style.width = `${width}px`;
            dom.canvas.style.height = `${height}px`;
        }
    };

    const attachVideoEvents = (dom, getClockContext = () => ({})) => {
        if (!dom.video) return;

        const updateMediaProgress = () => {
            const duration = dom.video.duration;
            const rawTime = dom.video.currentTime;
            const currentTime = Math.floor(rawTime);

            // Extraction des zones bufferisées pour l'indicateur visuel et calcul de la durée réelle reçue
            const bufferRanges = [];
            let maxBufferedEnd = 0;
            if (dom.video.buffered) {
                for (let i = 0; i < dom.video.buffered.length; i++) {
                    const bStart = dom.video.buffered.start(i);
                    const bEnd = dom.video.buffered.end(i);
                    bufferRanges.push({ start: bStart, end: bEnd });
                    if (bEnd > maxBufferedEnd) maxBufferedEnd = bEnd;
                }
            }

            const isLive = isNaN(duration) || duration === Infinity;
            // En direct, la durée maximale est basée sur les segments réellement téléchargés, pas sur une horloge virtuelle
            const clipDuration = isLive
                ? Math.floor(maxBufferedEnd || currentTime)
                : Math.floor(duration);

            let newMax = maxSeenDurationRef.current;
            if (clipDuration > maxSeenDurationRef.current) newMax = clipDuration;
            maxSeenDurationRef.current = newMax;

            const clockContext = getClockContext?.() || {};
            const rangeStart = dom.video.seekable?.length
                ? dom.video.seekable.start(0)
                : (bufferRanges[0]?.start ?? 0);
            const rangeEnd = Math.max(
                newMax,
                dom.video.seekable?.length
                    ? dom.video.seekable.end(dom.video.seekable.length - 1)
                    : 0,
                maxBufferedEnd
            );
            const sessionBufferRanges = mediaRangesToSessionElapsed({
                ranges: bufferRanges,
                cues: id3CuesTimelineRef.current,
                sessionStartTs: clockContext.sessionStartTs,
                fallbackOffset: clockContext.fallbackOffset,
            });
            const sessionMediaRange = mediaRangesToSessionElapsed({
                ranges: [{ start: rangeStart, end: rangeEnd }],
                cues: id3CuesTimelineRef.current,
                sessionStartTs: clockContext.sessionStartTs,
                fallbackOffset: clockContext.fallbackOffset,
            })[0] || null;

            setSlaveState(prev => ({
                ...prev,
                position: currentTime,
                duration: newMax,
                bufferRanges,
                sessionBufferRanges,
                sessionMediaRange,
            }));

            if (dom.video.paused) {
                renderSingleFrame(dom);
            }
        };
        mediaProgressUpdaterRef.current = updateMediaProgress;

        dom.video.ontimeupdate = updateMediaProgress;
        dom.video.onprogress = updateMediaProgress;
        dom.video.ondurationchange = updateMediaProgress;

        dom.video.onplay = () => drawFrame(dom);
        dom.video.onpause = () => {
            if (animationFrameIdRef.current) cancelAnimationFrame(animationFrameIdRef.current);
            renderSingleFrame(dom);
        };
        dom.video.onseeked = () => {
            renderSingleFrame(dom, true);
        };
        dom.video.onloadeddata = () => {
            updateMediaProgress();
            renderSingleFrame(dom, true);
        };

        if (dom.canvasWrapper && typeof ResizeObserver !== 'undefined' && !resizeObserverRef.current) {
            const ro = new ResizeObserver(() => {
                adaptCanvasToFullscreen(dom, Boolean(document.fullscreenElement?.contains(dom.canvas)));
                renderSingleFrame(dom, true);
            });
            ro.observe(dom.canvasWrapper);
            resizeObserverRef.current = ro;
        }

        if (dom.canvasWrapper) {
            dom.canvasWrapper.onclick = () => {
                if (dom.video && dom.video.paused) {
                    if (dom.video.muted) dom.video.muted = false;
                    dom.video.play().catch(() => {});
                }
                if (typeof dom.canvasWrapper.requestFullscreen === 'function') {
                    if (!document.fullscreenElement) {
                        dom.canvasWrapper.requestFullscreen().catch(() => {});
                    } else if (typeof document.exitFullscreen === 'function') {
                        document.exitFullscreen().catch(() => {});
                    }
                }
            };
        }
    };

    const getMediaHandlers = (dom, setStatusMessage) => ({
        onPlay: () => {
            const video = dom.video;
            if (!video) return;
            if (isFirstPlayRef.current) {
                isFirstPlayRef.current = false;
            }
            if (setStatusMessage) setStatusMessage(null);
            if (video.muted) video.muted = false;
            if (audioContextRef.current?.state === 'suspended') audioContextRef.current.resume();
            video.play();
        },
        onPause: () => {
            if (dom.video) dom.video.pause();
        },
        onStart: () => {
            setTimelineState(prev => ({ ...prev, value: 0.01 }));
            if (dom.video) dom.video.currentTime = 0.01;
        },
        onGoLive: () => {
            const video = dom.video;
            if (!video) return;
            const rawLivePosition = hlsRef.current?.liveSyncPosition;
            const hlsLivePosition = typeof rawLivePosition === 'number' && Number.isFinite(rawLivePosition)
                ? rawLivePosition
                : null;
            const seekableEnd = video.seekable?.length
                ? video.seekable.end(video.seekable.length - 1)
                : null;
            const duration = Number(video.duration);
            const target = hlsLivePosition !== null
                ? hlsLivePosition
                : (Number.isFinite(seekableEnd)
                    ? Math.max(video.seekable.start(0), seekableEnd - 0.5)
                    : (Number.isFinite(duration) ? Math.max(0, duration - 0.5) : null));

            if (target !== null && Math.abs(video.currentTime - target) > 1) {
                video.currentTime = target;
            }
            if (setStatusMessage) setStatusMessage(null);
            if (video.muted) video.muted = false;
            if (audioContextRef.current?.state === 'suspended') audioContextRef.current.resume();
            video.play().catch(() => { });
        },
        onTimelineInput: (val) => {
            setTimelineState(prev => ({
                ...prev,
                value: val,
                display: `⏱ ${formatTime(val)} / ${formatTime(prev.max)}`
            }));
            if (dom.onTimeSeek) dom.onTimeSeek(val, false);
        },
        onTimelineChange: (val) => {
            setTimelineState(prev => ({
                ...prev,
                value: val,
                display: `⏱ ${formatTime(val)} / ${formatTime(prev.max)}`
            }));
            if (dom.onTimeSeek) dom.onTimeSeek(val, true);
        },
        onVolumeChange: (val) => {
            if (!dom.video) return;
            if (dom.video.muted) dom.video.muted = false;

            // 1. Calcul du gain : linéaire 0 -> 1, jamais d'amplification au-delà du
            // signal d'origine. L'ancienne courbe exponentielle montait jusqu'à x100
            // (+40dB) dès val=100, ce qui sature/clippe le signal micro et produit un
            // son craquant/saccadé — pas une perte de paquets.
            let newGain = val / 100;

            // 2. Application immédiate sur le hardware
            if (gainNodeRef.current && audioContextRef.current) {
                if (audioContextRef.current.state === 'suspended') {
                    audioContextRef.current.resume();
                }
                const now = audioContextRef.current.currentTime;
                // On utilise setValueAtTime pour un changement franc sans accumuler de rampes
                gainNodeRef.current.gain.setValueAtTime(newGain, now);
            } else if (dom.video) {
                dom.video.volume = newGain;
            }

            // 3. Limitation des re-rendus React pour éviter de faire saccader la vidéo
            // On ne met à jour l'état que si la valeur a sensiblement changé ou après un court délai
            const currentTime = Date.now();
            if (!window._lastVolUpdate || (currentTime - window._lastVolUpdate > 50)) {
                window._lastVolUpdate = currentTime;
                setSlaveState(prev => ({ ...prev, volume: val }));
            }
        },
        onMediaSeek: (seconds) => {
            if (dom.video) dom.video.currentTime = seconds;
        },
        onMediaPlay: () => {
            if (dom.video) {
                if (audioContextRef.current?.state === 'suspended') audioContextRef.current.resume();
                dom.video.play().catch(() => { });
            }
        },
        onMediaPause: () => {
            if (dom.video) dom.video.pause();
        },
        onMediaStart: () => {
            if (dom.video) dom.video.currentTime = 0;
        },
        onMediaGoLive: () => {
            const video = dom.video;
            if (!video) return;
            const target = hlsRef.current?.liveSyncPosition;
            if (typeof target === 'number' && Number.isFinite(target)) video.currentTime = target;
            else if (video.seekable?.length) video.currentTime = video.seekable.end(video.seekable.length - 1);
            else if (Number.isFinite(video.duration)) video.currentTime = video.duration;
        }
    });

    const resumeAudio = () => {
        if (audioContextRef.current?.state === 'suspended') {
            audioContextRef.current.resume().catch(() => {});
        }
    };

    const cleanupMedia = () => {
        if (hlsRef.current) {
            hlsRef.current.destroy();
            hlsRef.current = null;
        }
        if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
            audioContextRef.current.close().catch(console.error);
        }
        audioContextRef.current = null;
        gainNodeRef.current = null;
        audioSourceRef.current = null;
        if (animationFrameIdRef.current) cancelAnimationFrame(animationFrameIdRef.current);
        animationFrameIdRef.current = null;
        mediaProgressUpdaterRef.current = null;
        if (mediaRetryTimerRef.current) clearTimeout(mediaRetryTimerRef.current);
        mediaRetryTimerRef.current = null;
        mediaRetryAttemptRef.current = 0;
        recentFragmentDurationsRef.current = [];
        mediaFrameVisibleRef.current = false;
        if (resizeObserverRef.current) {
            resizeObserverRef.current.disconnect();
            resizeObserverRef.current = null;
        }
        clearCanvas();
        id3SegmentsMapRef.current.clear();
        id3CuesTimelineRef.current = [];
        maxSeenDurationRef.current = 0;
        isMediaActiveRef.current = false;
        isFirstPlayRef.current = true;
    };

    return {
        timelineState,
        setTimelineState,
        slaveState,
        setSlaveState,
        rotationMode,
        toggleRotationMode,
        hlsRef,
        isMediaActiveRef,
        hasActiveMedia: () => Boolean(hlsRef.current || isMediaActiveRef.current || domRef.current?.video?.src),
        hlsLatencyRef,
        currentVideoTsRef,
        id3CuesTimelineRef,
        id3SegmentsMapRef,
        getRotationAtTime,
        getSessionElapsedAtMediaTime,
        getMediaTimeAtSessionElapsed,
        getBufferedAhead,
        getRequiredDirectBufferSeconds,
        getSafeDirectMediaTime,
        hasReceivedMediaData,
        ensureMediaLoading,
        setMediaFrameVisible,
        refreshMediaProgress: () => mediaProgressUpdaterRef.current?.(),
        // Angle réellement appliqué à l'image (relecture : largeur de la colonne vidéo).
        getAppliedRotation: () => smoothedRotationRef.current,
        renderSingleFrame,
        resumeAudio,
        initializeMedia,
        attachVideoEvents,
        adaptCanvasToFullscreen,
        getMediaHandlers,
        cleanupMedia,
        mediaUrl,
        isDualCameraAvailable,
        selectedCamera,
        switchCamera
    };
};
